import { api } from './api';
import { Company } from '../types/company';
import { CompanyCreate } from '../types/company';
import { logger } from '../utils/logger';

// Custom error class for authentication failures
export class AuthenticationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "AuthenticationError";
    }
}

class CompanyService {
    async getCompany(id: number): Promise<Company> {
        try {
            // Removed verbose API call log (company fetch)
            // Add timestamp to prevent browser caching
            const timestamp = new Date().getTime();
            const response = await api.get(`/api/v1/companies/${id}?_t=${timestamp}`, {
                headers: {
                    'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
                }
            });
            // Response logging suppressed for production
            return response.data;
        } catch (error: any) {
            logger.error('Error fetching company:', error);
            if (error.response) {
                if (error.response.status === 403) {
                    // Special handling for permission errors
                    throw new Error(`You don't have permission to access this company. Please contact your administrator if you believe this is an error.`);
                }
                throw new Error(`Server error (${error.response.status}): ${error.response.data?.detail || 'Failed to fetch company'}`);
            } else if (error.request) {
                throw new Error('Network error: Unable to reach the server. Please check your internet connection and try again.');
            }
            throw error;
        }
    }

    async listCompanies(): Promise<Company[]> {
        try {
            // Removed verbose API call log (list companies)
            // Add timestamp to prevent browser caching
            const timestamp = new Date().getTime();
            const response = await api.get(`/api/v1/companies/?_t=${timestamp}&bypass_cache=true`, {
                headers: {
                    'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
                }
            });
            // Response logging suppressed for production
            return response.data;
        } catch (error: any) {
            logger.error('Error fetching companies:', error);
            if (error.response) {
                throw new Error(`Server error (${error.response.status}): ${error.response.data?.detail || 'Failed to fetch companies'}`);
            } else if (error.request) {
                throw new Error('Network error: Unable to reach the server. Please check your internet connection and try again.');
            }
            throw error;
        }
    }

    // Alias for listCompanies used by TaxRegistration component
    async getCompanies(): Promise<Company[]> {
        return this.listCompanies();
    }

    async createCompany(data: CompanyCreate): Promise<Company> {
        try {
            // Removed verbose API call log (create company)
            
            // Ensure all required fields are present
            const requiredFields = ['name', 'tax_id', 'address_line1', 'city', 'state', 'zip_code'];
            const missingFields = requiredFields.filter(field => !data[field as keyof CompanyCreate]);
            
            if (missingFields.length > 0) {
                throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
            }
            
            // Format website for tax registration requirements
            let formattedWebsite = data.website || 'https://www.test-company.com';
            
            // Ensure URL has proper protocol
            if (!formattedWebsite.startsWith('http://') && !formattedWebsite.startsWith('https://')) {
                formattedWebsite = 'https://' + formattedWebsite;
            }
            
            // Ensure it has www prefix if not already present
            if (!formattedWebsite.startsWith('http://www.') && !formattedWebsite.startsWith('https://www.')) {
                formattedWebsite = formattedWebsite.replace('https://', 'https://www.');
                formattedWebsite = formattedWebsite.replace('http://', 'http://www.');
            }
            
            // Format phone number according to E.164 format
            let formattedPhone = data.phone || '+14155550199';
            
            // If it's not empty but doesn't start with +, format it
            if (data.phone && !data.phone.startsWith('+')) {
                // Remove non-digit characters
                const digitsOnly = data.phone.replace(/\D/g, '');
                
                // If it's a 10-digit US number, add +1
                if (digitsOnly.length === 10) {
                    formattedPhone = `+1${digitsOnly}`;
                } else if (digitsOnly.length > 10) {
                    // If it already has country code
                    formattedPhone = `+${digitsOnly}`;
                } else {
                    // Default to a valid number if format is invalid
                    formattedPhone = '+14155550199';
                }
            }
            
            // Ensure country is set to default US if not provided
            const companyData: any = {
                ...data,
                website: formattedWebsite,
                phone: formattedPhone,
                country: data.country || 'US',
                is_head_office: data.is_head_office !== undefined ? data.is_head_office : true
            };
            // Remove optional fields if empty to avoid backend 422 errors
            if (!data.email) {
                delete companyData.email;
            }
            if (!data.phone) {
                delete companyData.phone;
            }
            
            // Suppressed formatted company data log
            
            // CRITICAL FIX: Ensure authentication token is properly set before making the request
            const token = localStorage.getItem('token');
            if (!token) {
                throw new AuthenticationError('No authentication token found. Please log in again.');
            }
            
            // Set the authorization header explicitly for this request
            const response = await api.post('/api/v1/companies/', companyData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
                }
            });
            
            // Response logging suppressed for production
            
            // Force cache refresh after creating a new company
            await this.refreshCompanyData();
            
            return response.data;
        } catch (error: any) {
            logger.error('Error creating company:', error);
            
            // Enhanced error logging for debugging
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                // Detailed error response fields suppressed
                
                // Handle authentication errors (401/403 status)
                if (error.response.status === 401 || error.response.status === 403) {
                    logger.error('Authentication error detected. Token may be invalid or expired.');
                    throw new AuthenticationError('Your session has expired. Please log in again.');
                }
                
                // Handle validation errors (422 status)
                if (error.response.status === 422) {
                    const errorData = error.response.data;
                    
                    // Check if the detail field is an array of validation errors
                    if (errorData?.detail && Array.isArray(errorData.detail)) {
                        const validationErrors = errorData.detail.map((err: any) => 
                            `${err.loc ? err.loc.join('.') : 'Unknown field'}: ${err.msg}`
                        ).join('; ');
                        throw new Error(`Validation error: ${validationErrors}`);
                    }
                    
                    // Check if there's a detail string
                    if (errorData?.detail && typeof errorData.detail === 'string') {
                        throw new Error(`Validation error: ${errorData.detail}`);
                    }
                    
                    // Handle object-style validation errors
                    if (errorData?.validation_errors) {
                        const validationErrors = Object.entries(errorData.validation_errors)
                            .map(([field, messages]) => {
                                if (Array.isArray(messages)) {
                                    return `${field}: ${messages[0]}`;
                                } else if (typeof messages === 'string') {
                                    return `${field}: ${messages}`;
                                }
                                return `${field}: Invalid value`;
                            })
                            .join('; ');
                        throw new Error(`Validation error: ${validationErrors}`);
                    }
                    
                    // Generic validation error message
                    throw new Error(`Validation error: ${JSON.stringify(errorData)}`);
                }
                
                // Check for specific error cases
                if (error.response.status === 400) {
                    const errorDetail = error.response.data?.detail || '';
                    if (errorDetail.includes('already exists')) {
                        throw new Error(`A company with this tax ID already exists. Please use a different tax ID.`);
                    } else if (errorDetail.includes('Tax ID must be at least')) {
                        throw new Error(`Tax ID must be at least 3 characters long.`);
                    } else {
                        throw new Error(errorDetail || 'Invalid company data. Please check your information and try again.');
                    }
                }
                
                // Handle server errors
                if (error.response.status >= 500) {
                    throw new Error('Server error. Please try again later.');
                }
                
                // Handle other HTTP errors
                throw new Error(`Request failed with status ${error.response.status}: ${error.response.data?.detail || 'Unknown error'}`);
            } else if (error.request) {
                // The request was made but no response was received
                logger.error('Network error - no response received');
                throw new Error('Network error. Please check your internet connection and try again.');
            } else {
                // Something happened in setting up the request
                logger.error('Request setup error');
                throw new Error(`Request failed: ${error.message}`);
            }
        }
    }

    async updateCompany(id: number, data: Partial<Company>): Promise<Company> {
        try {
            // Removed verbose API call log (update company)
            const response = await api.put(`/api/v1/companies/${id}`, data);
            // Response logging suppressed for production
            
            // Force cache refresh after updating a company
            await this.refreshCompanyData();
            
            return response.data;
        } catch (error: any) {
            logger.error('Error updating company:', error);
            
            if (error.response) {
                // The request was made and the server responded with an error
                // Suppressed error response data log
                throw new Error(`Server error (${error.response.status}): ${error.response.data?.detail || 'Failed to update company'}`);
            } else if (error.request) {
                // The request was made but no response was received
                logger.error('No response received during update');
                throw new Error('Network error: Unable to reach the server. Please check your internet connection and try again.');
            }
            
            throw new Error(error.message || 'An unexpected error occurred while updating the company.');
        }
    }

    async deleteCompany(id: number): Promise<void> {
        try {
            // Removed verbose API call log (delete company)
            await api.delete(`/api/v1/companies/${id}`);
            // Suppressed delete success log
            
            // Force cache refresh after deleting a company
            await this.refreshCompanyData();
        } catch (error: any) {
            logger.error('Error deleting company:', error);
            
            if (error.response) {
                // The request was made and the server responded with an error
                // Suppressed error response data log
                throw new Error(`Server error (${error.response.status}): ${error.response.data?.detail || 'Failed to delete company'}`);
            } else if (error.request) {
                // The request was made but no response was received
                logger.error('No response received during delete');
                throw new Error('Network error: Unable to reach the server. Please check your internet connection and try again.');
            }
            
            throw new Error(error.message || 'An unexpected error occurred while deleting the company.');
        }
    }

    async refreshCompanyData(): Promise<void> {
        try {
            // Suppressed cache bypass refresh log
            // Clear any API cache
            await api.post('/api/v1/cache/clear');
            
            // Force clear browser cache by adding timestamp and bypass_cache parameters
            const timestamp = new Date().getTime();
            
            // Make a separate API call to force cache refresh
            await api.get(`/api/v1/companies/?_t=${timestamp}&bypass_cache=true`);
            
            // Suppressed refresh success log
        } catch (error: any) {
            logger.error('Error refreshing company data:', error);
            
            if (error.response) {
                // Log but don't throw to prevent cascading errors
                // Suppressed detailed refresh error data log
            }
        }
    }
}

export const companyService = new CompanyService();